import { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { ADC, TIA, type ImpedanceExec } from '@/lib/protobuf/generated/ediphor';

const TIA_OPTIONS: { value: TIA; label: string }[] = [
  { value: TIA.Resistor_200Ohm, label: '200 Ω' },
  { value: TIA.Resistor_1kOhm, label: '1 kΩ' },
  { value: TIA.Resistor_5kOhm, label: '5 kΩ' },
  { value: TIA.Resistor_10kOhm, label: '10 kΩ' },
  { value: TIA.Resistor_20kOhm, label: '20 kΩ' },
  { value: TIA.Resistor_40kOhm, label: '40 kΩ' },
  { value: TIA.Resistor_80kOhm, label: '80 kΩ' },
  { value: TIA.Resistor_160kOhm, label: '160 kΩ' },
];

const ADC_OPTIONS: { value: ADC; label: string }[] = [
  { value: ADC.Gain_1, label: '×1' },
  { value: ADC.Gain_1P5, label: '×1.5' },
  { value: ADC.Gain_2, label: '×2' },
  { value: ADC.Gain_4, label: '×4' },
  { value: ADC.Gain_9, label: '×9' },
];

/**
 * Fallback values matching the current default Measurement Profile ("Upper Respiratory
 * Multisensor", profile 10000000-0000-0000-0000-000000000002). Only used if this panel
 * renders before the real profile has loaded into the wizard store — keep in sync with
 * that profile's ImpedanceExecJson whenever it changes.
 */
const FALLBACK_EXEC = {
  amplitudeMV: [50],
  biasMV: [200],
  rtiaOhm: TIA.Resistor_10kOhm,
  adcGain: ADC.Gain_2,
  frequencyHz: [
    10000, 6787.07, 4606.75, 3127.47, 2124.32, 1444.77, 985.35, 675.75,
    467.94, 328.85, 235.65, 172.7, 129.49, 99.19, 77.41, 61.37, 49.26,
    39.93, 32.6, 26.75, 22.01, 18.13, 14.92, 12.25, 10,
  ],
};

/**
 * Collapsible panel shown on Step 2 that lets researchers tweak ImpedanceExec
 * parameters before the baseline measurement starts.
 *
 * Changes are applied to the wizard store's selectedProfile only — they are NOT
 * persisted to the database and apply to the current run only.
 */
export function ResearchParamsPanel() {
  const selectedProfile = useMeasurementWizardStore((s) => s.selectedProfile);
  const setSelectedProfile = useMeasurementWizardStore((s) => s.setSelectedProfile);

  const exec = selectedProfile?.impedanceExecJson;

  // Capture profile defaults on first render so Reset can restore them.
  const defaultsRef = useRef({
    amplitudeMV: exec?.amplitudeMV ?? FALLBACK_EXEC.amplitudeMV,
    frequencyHz: [...(exec?.frequencyHz ?? FALLBACK_EXEC.frequencyHz)],
    biasMV: exec?.biasMV ?? FALLBACK_EXEC.biasMV,
    rtiaOhm: exec?.rtiaOhm ?? FALLBACK_EXEC.rtiaOhm,
    adcGain: exec?.adcGain ?? FALLBACK_EXEC.adcGain,
  });

  // Local text state for numeric fields — applied on blur, not on every keystroke.
  const [amplitudeText, setAmplitudeText] = useState(() => String((exec?.amplitudeMV ?? FALLBACK_EXEC.amplitudeMV)[0]));
  const [biasText, setBiasText] = useState(() => String((exec?.biasMV ?? FALLBACK_EXEC.biasMV)[0]));
  const [freqText, setFreqText] = useState(() => (exec?.frequencyHz ?? FALLBACK_EXEC.frequencyHz).join(', '));
  const [freqError, setFreqError] = useState<string | null>(null);

  if (!selectedProfile || !exec) return null;

  const d = defaultsRef.current;

  const isAmplitudeModified = exec.amplitudeMV[0] !== d.amplitudeMV[0];
  const isBiasModified = exec.biasMV[0] !== d.biasMV[0];
  const isRtiaModified = exec.rtiaOhm !== d.rtiaOhm;
  const isAdcModified = exec.adcGain !== d.adcGain;
  const isFreqModified =
    JSON.stringify(exec.frequencyHz) !== JSON.stringify(d.frequencyHz);
  const isModified =
    isAmplitudeModified ||
    isBiasModified ||
    isRtiaModified ||
    isAdcModified ||
    isFreqModified;

  const patch = (fields: Partial<ImpedanceExec>) =>
    setSelectedProfile({
      ...selectedProfile,
      impedanceExecJson: { ...exec, ...fields },
    });

  // --- Amplitude ---

  const handleAmplitudeBlur = () => {
    const val = parseFloat(amplitudeText);
    if (isNaN(val) || val < 0 || val > 808.8) return;
    const rounded = Math.round(val * 10) / 10;
    patch({ amplitudeMV: [rounded] });
    setAmplitudeText(String(rounded));
  };

  // --- Bias ---

  const handleBiasBlur = () => {
    const val = parseFloat(biasText);
    if (isNaN(val) || val < -1100 || val > 1100) return;
    const rounded = Math.round(val * 10) / 10;
    patch({ biasMV: [rounded] });
    setBiasText(String(rounded));
  };

  // --- Frequencies ---

  const handleFreqBlur = () => {
    const parts = freqText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (parts.length === 0) {
      setFreqError('At least one frequency is required.');
      return;
    }

    const values = parts.map(Number);
    if (values.some(isNaN) || values.some((v) => v <= 0)) {
      setFreqError('All values must be positive numbers (Hz).');
      return;
    }

    setFreqError(null);
    patch({ frequencyHz: values });
  };

  // --- Reset ---

  const handleReset = () => {
    patch(d);
    setAmplitudeText(String(d.amplitudeMV[0]));
    setBiasText(String(d.biasMV[0]));
    setFreqText(d.frequencyHz.join(', '));
    setFreqError(null);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: isModified ? 'warning.main' : 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
        mt: 2,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          color={isModified ? 'warning.main' : 'text.secondary'}
        >
          Research: Override Parameters
          {isModified ? ' (modified — this run only)' : ' (this run only)'}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Amplitude */}
          <Box>
            <Typography variant="body2" gutterBottom>
              Amplitude (mV) &mdash; range 0 – 808.8
            </Typography>
            <TextField
              type="number"
              size="small"
              value={amplitudeText}
              onChange={(e) => setAmplitudeText(e.target.value)}
              onBlur={handleAmplitudeBlur}
              inputProps={{ min: 0, max: 808.8, step: 0.1 }}
              sx={{ width: 160 }}
              error={(() => { const v = parseFloat(amplitudeText); return isNaN(v) || v < 0 || v > 808.8; })()}
              helperText={(() => { const v = parseFloat(amplitudeText); return isNaN(v) || v < 0 || v > 808.8 ? 'Must be 0 – 808.8 mV' : ''; })()}
            />
          </Box>

          {/* Bias Voltage */}
          <Box>
            <Typography variant="body2" gutterBottom>
              Bias Voltage (mV) &mdash; range -1100 – 1100
            </Typography>
            <TextField
              type="number"
              size="small"
              value={biasText}
              onChange={(e) => setBiasText(e.target.value)}
              onBlur={handleBiasBlur}
              inputProps={{ min: -1100, max: 1100, step: 0.1 }}
              sx={{ width: 160 }}
              error={(() => { const v = parseFloat(biasText); return isNaN(v) || v < -1100 || v > 1100; })()}
              helperText={(() => { const v = parseFloat(biasText); return isNaN(v) || v < -1100 || v > 1100 ? 'Must be -1100 – 1100 mV' : ''; })()}
            />
          </Box>

          {/* TIA Resistor */}
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>TIA Resistor</InputLabel>
            <Select
              label="TIA Resistor"
              value={exec.rtiaOhm}
              onChange={(e) => patch({ rtiaOhm: e.target.value as TIA })}
            >
              {TIA_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ADC Gain */}
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel>ADC Gain</InputLabel>
            <Select
              label="ADC Gain"
              value={exec.adcGain}
              onChange={(e) => patch({ adcGain: e.target.value as ADC })}
            >
              {ADC_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Frequencies */}
          <Box>
            <Typography variant="body2" gutterBottom>
              Frequencies (Hz) &mdash; {exec.frequencyHz.length} point
              {exec.frequencyHz.length !== 1 ? 's' : ''}
            </Typography>
            <TextField
              multiline
              rows={5}
              fullWidth
              size="small"
              value={freqText}
              onChange={(e) => setFreqText(e.target.value)}
              onBlur={handleFreqBlur}
              placeholder="e.g. 10, 100, 1000, 10000"
              error={!!freqError}
              helperText={freqError ?? 'Comma or space-separated Hz values — applied on focus loss'}
            />
          </Box>

          {isModified && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              These overrides apply to this measurement only and are not saved to the profile.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleReset}
              disabled={!isModified}
            >
              Reset to profile defaults
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
